// A small router with iOS habits: push and pop are distinct, pop animates
// the other way, and every screen is a lazy chunk. The route table is the
// children of <Router>; each <Route> pairs a pattern with a loader.
import { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useState, type ComponentType, type ReactElement, type ReactNode } from 'react';

type Params = Record<string, string>;
type Screen = ComponentType<{ params: Params }>;
type Loader = () => Promise<{ default: Screen }>;
/** A screen is a lazy chunk (`load`), or in the shell itself (`component`) when it is the first thing seen. */
interface RouteProps { path: string; load?: Loader; component?: Screen }
interface Nav { path: string; params: Params; push: (to: string) => void; replace: (to: string) => void; back: () => void }

export function Route(_props: RouteProps): null { return null; }

const NavContext = createContext<Nav | null>(null);
export function useNav(): Nav {
  const nav = useContext(NavContext);
  if (!nav) throw new Error('useNav outside <Router>');
  return nav;
}

// Depth lets pop be told from push without a history listener per screen.
const depthKey = 'hd:depth';
const depth = () => (history.state as { depth?: number } | null)?.depth ?? 0;

function transition(direction: 'push' | 'pop' | 'replace', update: () => void) {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => void };
  if (!doc.startViewTransition || matchMedia('(prefers-reduced-motion: reduce)').matches) return update();
  document.documentElement.dataset.nav = direction;
  doc.startViewTransition(update);
}

function match(pattern: string, path: string): Params | null {
  const a = pattern.split('/').filter(Boolean), b = path.split('/').filter(Boolean);
  if (a.length !== b.length) return null;
  const params: Params = {};
  for (let i = 0; i < a.length; i++) {
    const p = a[i]!, v = b[i]!;
    if (p.startsWith(':')) params[p.slice(1)] = decodeURIComponent(v);
    else if (p !== v) return null;
  }
  return params;
}

export function Router({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const [path, setPath] = useState(() => location.pathname);
  const routes = useMemo(() => {
    const list = (Array.isArray(children) ? children : [children]) as ReactElement<RouteProps>[];
    return list.filter(Boolean).map((el) => {
      const { path, load, component } = el.props;
      if (!component && !load) throw new Error(`route ${path} has no screen`);
      return { path, Component: component ?? lazy(load!) };
    });
  }, [children]);

  useEffect(() => {
    const onPop = () => transition(depth() < lastDepth ? 'pop' : 'push', () => setPath(location.pathname));
    let lastDepth = depth();
    const track = () => { lastDepth = depth(); };
    addEventListener('popstate', () => { onPop(); track(); });
    return () => removeEventListener('popstate', onPop);
  }, []);

  const push = useCallback((to: string) => {
    history.pushState({ depth: depth() + 1 }, '', to);
    sessionStorage.setItem(depthKey, String(depth()));
    transition('push', () => setPath(to));
  }, []);
  const replace = useCallback((to: string) => {
    history.replaceState({ depth: depth() }, '', to);
    transition('replace', () => setPath(to));
  }, []);
  const back = useCallback(() => {
    if (depth() > 0) history.back();
    else replace('/');
  }, [replace]);

  const hit = routes.flatMap((r) => { const params = match(r.path, path); return params ? [{ r, params }] : []; })[0];
  const nav = useMemo<Nav>(() => ({ path, params: hit?.params ?? {}, push, replace, back }), [path, hit?.params, push, replace, back]);
  if (!hit) return <NavContext.Provider value={nav}><NotFound /></NavContext.Provider>;
  const { Component } = hit.r;
  return (
    <NavContext.Provider value={nav}>
      <Suspense fallback={fallback}><Component params={hit.params} /></Suspense>
    </NavContext.Provider>
  );
}

function NotFound() {
  const { replace } = useNav();
  return (
    <main className="app paper" style={{ padding: 24 }}>
      <h1>Nothing here.</h1>
      <p><button type="button" className="btn honey" onClick={() => replace('/')}>Back to your projects</button></p>
    </main>
  );
}
